import { Fragment, useState } from "react";
import { cn } from "@/lib/cn";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { CollectionStatus, CellStatus } from "@/types/billing";

const UTILITY_LABELS: Record<string, string> = {
  "zimna-woda":         "Zimna woda",
  "podgrzanie-cw":      "Ciepła woda",
  "oplata-stała-co-cw": "Opłata stała CO CW",
  "en-el":              "Prąd",
  "gaz":                "Gaz",
  "co":                 "CO",
  "smieci":             "Śmieci",
  "internet":           "Internet",
  "settlement":         "Rozliczenie CO",
};

const STATUS_TONE: Record<CellStatus, "success" | "warning" | "muted"> = {
  collected:  "success",
  missing:    "warning",
  estimated:  "muted",
  optional:   "muted",
  "n/a":      "muted",
};

const STATUS_LABEL: Record<CellStatus, string> = {
  collected:  "Zebrano",
  missing:    "Brakujące",
  estimated:  "Oszacowane",
  optional:   "Opcjonalne",
  "n/a":      "N/D",
};

// Active-chip colours match each status tone.
const CHIP_ACTIVE: Record<CellStatus, string> = {
  collected:  "bg-success/15 text-success border-success/30",
  missing:    "bg-warning/15 text-warning border-warning/40",
  estimated:  "bg-bg-subtle text-text border-border",
  optional:   "bg-bg-subtle text-text border-border",
  "n/a":      "bg-bg-subtle text-text border-border",
};

const ALL_STATUSES: CellStatus[] = ["missing", "collected", "estimated", "optional", "n/a"];

function shortDate(iso: string | null) {
  if (!iso) return null;
  return iso.slice(5, 10).replace("-", ".");
}

export function listMissing(status: CollectionStatus): string[] {
  const out: string[] = [];
  for (const [loc, cells] of Object.entries(status)) {
    for (const [util, cell] of Object.entries(cells)) {
      if (cell.status === "missing") out.push(`${loc} / ${util}`);
    }
  }
  return out;
}

interface MissingSummaryProps {
  status: CollectionStatus;
  year?: number;
  month?: number;
  refetch?: () => void;
}

export function MissingSummary({ status, year, month, refetch }: MissingSummaryProps) {
  const [visible, setVisible] = useState<Set<CellStatus>>(new Set(["missing"]));
  const { mutate } = useApiMutation();
  const { push } = useToast();

  async function deleteEstimate(location: string, utility: string) {
    if (year === undefined || month === undefined) return;
    try {
      await mutate("/api/billing/collection/entry", "DELETE", { location, utility, year, month });
      push({ message: `Usunięto szacunek ${location} / ${utility}`, tone: "success" });
      refetch?.();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  function toggle(s: CellStatus) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        if (next.size > 1) next.delete(s); // keep at least one active
      } else {
        next.add(s);
      }
      return next;
    });
  }

  function showAll() {
    setVisible(new Set(ALL_STATUSES));
  }

  // Count items per status across all locations
  const counts: Partial<Record<CellStatus, number>> = {};
  for (const cells of Object.values(status)) {
    for (const cell of Object.values(cells)) {
      counts[cell.status] = (counts[cell.status] ?? 0) + 1;
    }
  }

  // Only show statuses that have at least one item
  const presentStatuses = ALL_STATUSES.filter((s) => (counts[s] ?? 0) > 0);

  const missingCount = counts["missing"] ?? 0;

  // Apply filter: group by location, drop invisible rows, drop empty locations
  const filteredLocations = Object.entries(status)
    .map(([loc, cells]) => ({
      loc,
      rows: Object.entries(cells).filter(([, cell]) => visible.has(cell.status)),
    }))
    .filter(({ rows }) => rows.length > 0);

  const allVisible = presentStatuses.every((s) => visible.has(s));

  return (
    <Card padding="none">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-semibold">Stan zbierania</h3>
        {missingCount > 0
          ? <Badge tone="warning">{missingCount} brakujących</Badge>
          : <Badge tone="success">Kompletne</Badge>
        }
      </div>

      {/* Filter chips */}
      <div className="px-4 py-2 border-b border-border flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-text-muted mr-1">Filtr:</span>
        {presentStatuses.map((s) => {
          const active = visible.has(s);
          return (
            <button
              key={s}
              onClick={() => toggle(s)}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                active
                  ? CHIP_ACTIVE[s]
                  : "border-border text-text-muted hover:border-text-muted hover:text-text",
              )}
            >
              {STATUS_LABEL[s]}
              <span className={cn("tabular-nums", active ? "opacity-70" : "opacity-50")}>
                {counts[s]}
              </span>
            </button>
          );
        })}
        {!allVisible && (
          <button
            onClick={showAll}
            className="ml-auto text-xs text-text-muted underline-offset-2 hover:underline"
          >
            Pokaż wszystkie
          </button>
        )}
      </div>

      {/* Table */}
      {filteredLocations.length === 0 ? (
        <p className="px-4 py-4 text-xs text-text-muted italic">
          Brak wierszy spełniających aktywny filtr.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted">
            <tr className="border-b border-border">
              <th className="text-left font-medium px-4 py-2">Media</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Źródło</th>
              <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Zebrano</th>
              <th className="text-left font-medium px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {filteredLocations.map(({ loc, rows }) => (
              <Fragment key={loc}>
                <tr className="border-b border-border">
                  <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide bg-bg-subtle capitalize">
                    {loc}
                  </td>
                </tr>
                {rows.map(([util, cell]) => (
                  <tr
                    key={`${loc}/${util}`}
                    className={cn(
                      "border-b border-border last:border-0",
                      cell.status === "missing" && "bg-warning/5",
                    )}
                  >
                    <td className="px-4 py-2 text-xs pl-6">
                      {UTILITY_LABELS[util] ?? util}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={STATUS_TONE[cell.status]}>{STATUS_LABEL[cell.status]}</Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-text-muted hidden sm:table-cell">
                      {cell.source ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-text-muted hidden sm:table-cell">
                      {shortDate(cell.collected_at) ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {cell.status === "estimated" && (
                        <button
                          onClick={() => deleteEstimate(loc, util)}
                          className="text-xs text-danger hover:underline"
                        >
                          Usuń szacunek
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
